import type { ResultEnvelope } from "./types";

export interface ShowcaseEntry {
  id: string;
  title: string;
  component: string;
  note?: string;
  hidden?: boolean;
  rendererPath: string;
  data: ResultEnvelope["data"];
}

function svgImage(label: string, bg = "#ffffff", fg = "#111111") {
  const safeLabel = encodeURIComponent(label);
  const safeBg = encodeURIComponent(bg);
  const safeFg = encodeURIComponent(fg);
  return `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='560' viewBox='0 0 800 560'%3E%3Crect width='800' height='560' fill='${safeBg}'/%3E%3Crect x='110' y='90' width='580' height='320' rx='20' fill='${safeFg}' opacity='0.95'/%3E%3Crect x='140' y='120' width='520' height='250' rx='12' fill='%23212121'/%3E%3Ctext x='400' y='470' font-size='34' text-anchor='middle' fill='${safeFg}' font-family='Arial, sans-serif'%3E${safeLabel}%3C/text%3E%3C/svg%3E`;
}

function heroSlides(prefix: string) {
  return [
    { medium: svgImage(`${prefix} Front`) },
    { medium: svgImage(`${prefix} Side`) },
  ];
}

const price = {
  currencySymbol: "£",
  amount: 1549,
  originalAmount: 1799,
  discountPct: 14,
};

const altPrice = {
  currencySymbol: "£",
  amount: 1299,
  originalAmount: 1499,
  discountPct: 13,
};

export const showcaseEntries: ShowcaseEntry[] = [
  {
    id: "product-detail",
    title: "Product detail",
    component: "product_detail",
    rendererPath: "prototype/frontend/widget/src/cards/ProductDetailCard.tsx",
    data: {
      sku: "OLED55C4PLA",
      name: "LG OLED evo C4 55\" 4K Smart TV",
      category: "TV",
      rating: 4.6,
      reviewCount: 1284,
      price,
      image: svgImage("OLED55C4PLA"),
      images: heroSlides("OLED"),
      badges: [{ label: "New" }, { label: "Save £250", tone: "deal" }],
      keySpecs: [
        { name: "Screen size", value: "55\"" },
        { name: "Panel", value: "OLED evo" },
        { name: "Processor", value: "α9 Gen7" },
      ],
      pdpUrl: "https://www.lg.com/uk/tvs-soundbars/oled-evo/oled55c4pla/",
    },
  },
  {
    id: "product-detail-notsold",
    title: "Product detail — 미판매(참고카드)",
    component: "product_detail",
    rendererPath: "prototype/frontend/widget/src/cards/ProductDetailCard.tsx",
    note: "커머스 미온보딩/단종 참고카드 — 가격 영역 미노출·구매 버튼 없음 (LGCOMMIE-8041)",
    data: {
      sku: "LF25NZH22S",
      name: "36\" 25 cu. ft. Zero Clearance Counter-Depth MAX French 3-Door Refrigerator",
      category: "Refrigerators",
      rating: null,
      reviewCount: 0,
      price: null,
      salesStatus: "NOT_SOLD",
      image: svgImage("LF25NZH22S"),
      images: heroSlides("Refrigerator"),
      badges: [],
      featureBullets: [
        "Zero Clearance Hinge System",
        "Counter-Depth MAX, 25 cu. ft.",
        "Door Cooling+",
        "Factory Installed Automatic Ice Maker",
      ],
      pdpUrl: "https://www.lg.com/ca_en/refrigerators/french-door/lf25nzh22s/",
    },
  },
  {
    id: "promotion-list",
    title: "Promotion list",
    component: "promotion_list",
    rendererPath: "prototype/frontend/widget/src/cards/PromotionListCard.tsx",
    data: {
      highlightRootCodes: ["RC_SOUNDBAR"],
      promotions: [
        {
          id: "promo-1",
          rootCodes: ["RC_SOUNDBAR"],
          title: "Up to 50% off Selected Soundbars",
          subtitle: "when you buy an eligible TV",
          startsAt: "2026-04-01",
          endsAt: "2026-04-30",
          linkUrl: "https://www.lg.com/uk/offers/",
          ctaLabel: "Redeem Now",
        },
        {
          id: "promo-2",
          title: "Unmissable savings on the US80TR",
          // subtitle 없음 — AISASSIT-35 검증: 설명 없어도 2줄(info-desc) 영역 고정 확인용
          startsAt: "2026-05-01",
          endsAt: "2026-05-31",
          linkUrl: "https://www.lg.com/uk/offers/",
          ctaLabel: "Redeem Now",
        },
        {
          id: "promo-3",
          title: "Free wall mounting on OLED G series - wall mount version",
          subtitle:
            "Get free wall mounting service worth £120 when you buy an OLED G Series. Click wall mount service under Additional Services.",
          linkUrl: "https://www.lg.com/uk/offers/buy/",
          ctaLabel: "Buy Now",
          optionalCtaUrl: "https://www.lg.com/uk/offers/redeem/",
          optionalCtaLabel: "Redeem Now",
        },
        {
          id: "promo-4",
          title: "Trade-in bonus: extra £150 on TVs",
          subtitle: "Send us your old set, save more",
          startsAt: "2026-06-01",
          endsAt: "2026-06-30",
          linkUrl: "https://www.lg.com/uk/offers/",
          ctaLabel: "Redeem Now",
        },
      ],
    },
  },
  {
    id: "review-summary",
    title: "Review summary",
    component: "review_summary",
    rendererPath: "prototype/frontend/widget/src/cards/ReviewSummaryCard.tsx",
    data: {
      sku: "OLED55C4PLA",
      productName: "LG OLED evo C4 55\"",
      averageRating: 4.9,
      totalReviews: 12931,
      ratingDistribution: { 5: 924, 4: 231, 3: 77, 2: 28, 1: 24 },
      secondaryRatings: [
        { id: "features", label: "Features", averageRating: 4.5, valueRange: 5 },
        { id: "performance", label: "Performance", averageRating: 4.4, valueRange: 5 },
        { id: "design", label: "Design", averageRating: 4.6, valueRange: 5 },
        { id: "quality", label: "Quality", averageRating: 4.5, valueRange: 5 },
        { id: "value", label: "Value", averageRating: 4.3, valueRange: 5 },
      ],
      reviewsSectionUrl: "https://www.lg.com/uk/tvs-soundbars/oled-evo/oled55c4pla/reviews",
    },
  },
  {
    id: "cart-added",
    title: "Cart added",
    component: "cart_added",
    rendererPath: "prototype/frontend/widget/src/cards/CartAddedCard.tsx",
    data: {
      cartUrl: "https://www.lg.com/uk/cart",
      items: [
        {
          sku: "OLED55C4PLA",
          productName: "LG OLED evo C4 55\" 4K Smart TV",
          image: svgImage("OLED55C4PLA"),
          price,
          quantity: 1,
        },
      ],
    },
  },
  {
    id: "cart-added-multi",
    title: "Cart added (multi)",
    component: "cart_added",
    rendererPath: "prototype/frontend/widget/src/cards/CartAddedCard.tsx",
    note: "기획 §3-2 다건: 단일 카드 안 세로 리스트 + 하단 Go to Basket 1개(캐러셀 아님).",
    data: {
      cartUrl: "https://www.lg.com/uk/cart",
      items: [
        {
          sku: "OLED65G54LW",
          productName: "65 inch LG OLED evo AI G5 4K Smart TV 2025 - Stand version",
          image: svgImage("OLED65G54LW"),
          price,
          quantity: 1,
        },
        {
          sku: "QNED86A1A6A",
          productName: "LG QNED86 55\" 4K Smart TV",
          image: svgImage("QNED86A1A6A", "#f2f5ec"),
          price: altPrice,
          quantity: 1,
        },
        {
          sku: "MZ-070NW",
          productName: "20L 700W NeoChef™ Slim Microwave Oven with Touch Control",
          image: svgImage("MZ-070NW", "#eef2f6"),
          price: altPrice,
          quantity: 1,
        },
      ],
    },
  },
  {
    id: "coupon-list",
    title: "Coupon list",
    component: "coupon_list",
    rendererPath: "prototype/frontend/widget/src/cards/CouponListCard.tsx",
    data: {
      cartUrl: "https://www.lg.com/uk/cart",
      coupons: [
        {
          code: "OLED10",
          name: "10% off OLED TVs",
          description: "Apply to eligible OLED models over £999.",
          expiresAt: "2026-07-25",
          status: "available",
        },
        {
          code: "FREESHIP",
          name: "Free Delivery",
          description: "Standard delivery on qualifying products.",
          expiresAt: "2026-07-15",
          status: "member_only",
        },
        {
          code: "WELCOME5",
          name: "Welcome Coupon 5% OFF",
          // More/Less 확인용 긴 설명(≥130자) — 3줄 클램프 후 More 버튼 노출.
          description:
            "Apply to eligible OLED and QNED models over £999. Maximum discount amount is £1,000 per order. Cannot be combined with other promotions, member-exclusive offers, or clearance items. Valid online only.",
          expiresAt: "2026-07-20",
          status: "available",
        },
      ],
    },
  },
  {
    id: "coupon-list-scroll",
    title: "Coupon list — long desc (scroll)",
    component: "coupon_list",
    note: "More 펼침 시 설명이 max-height(112px)를 넘으면 info-desc-detail 내부 스크롤.",
    rendererPath: "prototype/frontend/widget/src/cards/CouponListCard.tsx",
    data: {
      cartUrl: "https://www.lg.com/uk/cart",
      coupons: [
        {
          code: "TERMS20",
          name: "20% off — full terms",
          // 스크롤 테스트: 펼쳤을 때 max-height(112px)를 넘는 초장문 → info-desc-detail.on 내부 스크롤 노출.
          description:
            "Save 20% on selected LG home appliances. Applies to washing machines, dryers, dishwashers and refrigerators over £799 only. Maximum discount is £400 per order and one coupon per customer per calendar month. Cannot be combined with any other promotion, member-exclusive pricing, trade-in bonus, bundle deal, or clearance/outlet items. Excludes installation, delivery and recycling fees. Valid online at lg.com only; not redeemable in retail stores. LG reserves the right to amend or withdraw this offer at any time. Subject to stock availability while supplies last.",
          expiresAt: "2026-08-31",
          status: "available",
        },
      ],
    },
  },
  {
    id: "my-coupon-guest",
    title: "My coupons — guest (Sign in)",
    component: "my_coupon_list",
    note: "AISASSIT-114: 비회원(loginRequired) Sign in 버튼 — DS Button primary(Button-Contained).",
    rendererPath: "prototype/frontend/widget/src/cards/CouponListCard.tsx",
    data: {
      title: "My Coupons",
      loginRequired: true,
      loginUrl: "https://www.lg.com/uk/login",
      coupons: [],
    },
  },
  {
    id: "secret-coupon",
    title: "Secret coupon",
    component: "secret_coupon",
    note: "Wrapper around the same renderer as coupon_list.",
    rendererPath: "prototype/frontend/widget/src/cards/SecretCouponCard.tsx",
    data: {
      coupons: [
        {
          code: "SECRETVIP15",
          name: "VIP secret coupon",
          description: "Private offer unlocked during chat.",
          expiresAt: "2026-08-10",
          status: "available",
        },
      ],
    },
  },
  {
    id: "shipping-methods",
    title: "Shipping methods",
    component: "shipping_methods",
    hidden: true,
    rendererPath: "prototype/frontend/widget/src/cards/ShippingMethodsCard.tsx",
    data: {
      selectedMethodId: "standard",
      methods: [
        { id: "standard", name: "Standard delivery", estimatedDays: "2-4 business days", fee: 0, currencySymbol: "£" },
        { id: "next-day", name: "Next day delivery", estimatedDays: "Tomorrow", fee: 19.99, currencySymbol: "£" },
      ],
    },
  },
  {
    id: "order-list",
    title: "Order list",
    component: "order_list",
    rendererPath: "prototype/frontend/widget/src/cards/OrderListCard.tsx",
    data: {
      orders: [
        {
          orderNumber: "UK240611-1024",
          placedAt: "2026-06-11",
          status: "on_delivery",
          statusLabel: "On Delivery",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240611-1024",
          items: [
            {
              sku: "OLED55C4PLA",
              name: "LG OLED evo C4 55\" 4K Smart TV",
              imageUrl: svgImage("OLED55C4PLA"),
              quantity: 1,
              deliveryStep: 4,
              estimatedDelivery: "2026-06-02",
              options: ["Voltage 12V", "Qty 1"],
            },
          ],
        },
        {
          orderNumber: "UK240520-0788",
          placedAt: "2026-05-20",
          status: "complete",
          statusLabel: "Delivery Completed",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240520-0788",
          items: [
            {
              sku: "OLED65G54LW",
              name: "65 inch LG OLED evo AI G5 4K Smart TV 2025",
              imageUrl: svgImage("OLED65G54LW"),
              quantity: 1,
              deliveryStep: 5,
              estimatedDelivery: "2026-05-02",
              options: ["Voltage 12V", "Qty 1"],
            },
          ],
        },
        {
          orderNumber: "795987950587",
          placedAt: "2026-05-05",
          status: "awaiting_contract",
          statusLabel: "Awaiting Contract",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/795987950587",
          items: [
            {
              sku: "GSXV91MCAE",
              name: "LG Premium Care 1 Year",
              itemType: "ew",
              modelNumber: "GSXV91MCAE",
              serialNumber: "209NTDVGV111",
              imageUrl: svgImage("Warranty"),
              quantity: 1,
            },
          ],
        },
        {
          orderNumber: "UK240602-2210",
          placedAt: "2026-05-05",
          status: "preparing_for_delivery",
          statusLabel: "Preparing for delivery",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240602-2210",
          items: [
            {
              sku: "OLED65G54LW",
              name: "65 inch LG QNED evo AI QNED86 MiniLED 4K Smart TV 2025",
              imageUrl: svgImage("Bundle"),
              quantity: 1,
              options: ["RPM : 1400", "Qty 1"],
              bundleChildren: [
                {
                  sku: "OLED65G54LW",
                  name: "65 inch LG QNED evo AI QNED86 MiniLED 4K Smart TV 2025",
                  imageUrl: svgImage("Washer"),
                  quantity: 1,
                  deliveryStep: 2,
                  estimatedDelivery: "2026-06-02",
                  options: ["Weight : 11kg", "Qty 1"],
                },
                {
                  sku: "OLED65G54LW",
                  name: "65 inch LG QNED evo AI QNED86 MiniLED 4K Smart TV 2025",
                  imageUrl: svgImage("Dryer"),
                  quantity: 1,
                  deliveryStep: 2,
                  estimatedDelivery: "2026-06-02",
                  options: ["Weight : 11kg", "Qty 1"],
                },
              ],
            },
          ],
        },
        {
          orderNumber: "UK240603-3310",
          placedAt: "2026-05-05",
          status: "preparing_for_delivery",
          statusLabel: "Preparing for delivery",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240603-3310",
          items: [
            {
              sku: "OLED65G54LW",
              name: "StanbyME 2 Screen & XT7S Speaker Bundle",
              imageUrl: svgImage("StanbyME"),
              quantity: 1,
              deliveryStep: 2,
              estimatedDelivery: "2026-06-02",
              options: ["Voltage : 127v", "Qty 1"],
              packageDealId: "PKG-1",
            },
            {
              sku: "OLED65G54LW",
              name: "LG LED65C25LB.AEU",
              imageUrl: svgImage("Soundbar"),
              quantity: 1,
              deliveryStep: 2,
              estimatedDelivery: "2026-06-02",
              options: ["Weight : 11kg", "Qty 1"],
              packageDealId: "PKG-1",
            },
          ],
        },
        {
          orderNumber: "UK240604-4410",
          placedAt: "2026-05-05",
          status: "preparing_for_delivery",
          statusLabel: "Preparing for delivery",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240604-4410",
          items: [
            {
              sku: "OLED65G54LW",
              name: '17" LG gram Pro, Windows 11 Home, 32GB RAM, Intel® Arrow Lake',
              imageUrl: svgImage("Gram"),
              quantity: 1,
              deliveryStep: 2,
              estimatedDelivery: "2026-06-02",
              options: ["Inch 97", "Qty 1"],
              addons: [
                {
                  sku: "OLED65G54LW",
                  name: "LG xboom Buds by will.i.am | Bluetooth Earbuds",
                  imageUrl: svgImage("Buds"),
                  quantity: 1,
                  deliveryStep: 2,
                  estimatedDelivery: "2026-06-02",
                  options: ["Voltage: 127v", "Qty 1"],
                },
                {
                  sku: "OLED65G54LW",
                  name: "LG xboom Grab by will.i.am | Bluetooth Speaker",
                  imageUrl: svgImage("Speaker"),
                  quantity: 1,
                  deliveryStep: 2,
                  estimatedDelivery: "2026-06-02",
                  options: ["Weight : 11kg", "Qty 1"],
                },
              ],
            },
          ],
        },
        {
          orderNumber: "UK240605-5510",
          placedAt: "2026-05-05",
          status: "preparing_for_delivery",
          statusLabel: "Preparing for delivery",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240605-5510",
          items: [
            {
              sku: "OLED65G54LW",
              name: "635L InstaView™ American Style Fridge Freezer Matte Black Total",
              itemType: "omv",
              imageUrl: svgImage("Fridge"),
              quantity: 1,
              deliveryStep: 2,
              estimatedDelivery: "2026-06-02",
              options: ["Size : 635L", "Qty 1"],
            },
          ],
        },
        {
          orderNumber: "UK240606-6610",
          placedAt: "2026-05-05",
          status: "preparing_for_delivery",
          statusLabel: "Preparing for delivery",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240606-6610",
          items: [
            {
              sku: "OLED65G54LW",
              name: "65 inch LG QNED evo AI QNED86 MiniLED 4K Smart TV 2025",
              imageUrl: svgImage("TV"),
              quantity: 1,
              deliveryStep: 4,
              estimatedDelivery: "2026-06-02",
              trackingUrl: "https://www.lg.com/uk/track/UK240606-6610",
              options: ["Voltage: 127v", "Qty 1"],
            },
            {
              sku: "OLED65G54LW",
              name: "635L InstaView™ American Style Fridge Freezer Matte Black Total",
              imageUrl: svgImage("Fridge2"),
              quantity: 1,
              deliveryStep: 2,
              estimatedDelivery: "2026-06-02",
              options: ["Size : 635L", "Qty 1"],
            },
          ],
        },
        {
          orderNumber: "UK240607-7710",
          placedAt: "2026-05-05",
          status: "preparing_for_delivery",
          statusLabel: "Preparing for delivery",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240607-7710",
          items: [
            {
              sku: "OLED65G54LW",
              name: "635L InstaView™ American Style Fridge Freezer Matte Black Total",
              imageUrl: svgImage("Fridge3"),
              quantity: 1,
              deliveryStep: 2,
              estimatedDelivery: "2026-06-02",
              options: ["Inch 97", "Qty 1"],
              returns: [
                "Qty : 1 / Return approved / 88000000777",
                "Qty : 1 / Return approved / 88000000778",
              ],
            },
          ],
        },
        {
          orderNumber: "UK240608-8810",
          placedAt: "2026-05-05",
          status: "cancelled",
          statusLabel: "Cancel",
          orderDetailUrl: "https://www.lg.com/uk/mypage/orders/UK240608-8810",
          items: [
            {
              sku: "OLED65G54LW",
              name: "635L InstaView™ American Style Fridge Freezer Matte Black Total",
              imageUrl: svgImage("Fridge4"),
              quantity: 1,
              options: ["Inch 97", "Qty 1"],
            },
          ],
        },
      ],
    },
  },
  {
    id: "delivery-tracking",
    title: "Delivery tracking",
    component: "delivery_tracking",
    hidden: true,
    rendererPath: "prototype/frontend/widget/src/cards/DeliveryTrackingCard.tsx",
    data: {
      orderNumber: "UK240611-1024",
      status: "Out for delivery",
      delivery: { carrier: "DPD", trackingNumber: "DPD-2211009988" },
      timeline: [
        { label: "Order placed", done: true },
        { label: "Packed", done: true },
        { label: "Shipped", done: true },
        { label: "Out for delivery", done: true },
        { label: "Delivered", done: false },
      ],
    },
  },
  {
    id: "postcode-input",
    title: "Postal Code",
    component: "postcode_input",
    rendererPath: "prototype/frontend/widget/src/cards/PostcodeInputCard.tsx",
    data: {
      fields: [{ id: "post_code", type: "text", required: true, wide: true }],
      scenario: "shipping",
    },
  },
  {
    id: "track-orders-form",
    title: "Track my orders form",
    component: "track_my_orders_form",
    rendererPath: "prototype/frontend/widget/src/cards/TrackMyOrdersFormCard.tsx",
    data: {
      headline: "Track your order",
      orderNumberLabel: "Order number",
      orderNumberPlaceholder: "e.g. UK240611-1024",
      emailLabel: "Email",
      emailPlaceholder: "name@example.com",
      submitLabel: "Track order",
    },
  },
  {
    id: "address-form",
    title: "Address form",
    component: "address_form",
    hidden: true,
    rendererPath: "prototype/frontend/widget/src/cards/AddressFormCard.tsx",
    data: {
      title: "Shipping address",
      subtitle: "Enter the delivery details for your order.",
      submitLabel: "Use this address",
      country: { countryName: "United Kingdom", countryCode: "GB" },
      fields: [
        { id: "firstName", label: "First name", required: true },
        { id: "lastName", label: "Last name", required: true },
        { id: "street", label: "Street", required: true, wide: true },
        { id: "city", label: "City", required: true },
        { id: "postcode", label: "Postcode", required: true },
        { id: "phone", label: "Phone", type: "tel" },
      ],
      prefilled: { firstName: "Minji", lastName: "Kim", city: "London" },
    },
  },
  {
    id: "login-form",
    title: "Login form",
    component: "login_form",
    rendererPath: "prototype/frontend/widget/src/cards/LoginFormCard.tsx",
    data: {
      title: "Sign in to continue",
      subtitle: "Use your LG account to access member-only benefits.",
      submitLabel: "Sign in",
      demoNote: "Demo only: submits a postMessage action to the host.",
      fields: [
        { id: "email", label: "Email", type: "email", required: true, wide: true },
        { id: "password", label: "Password", type: "password", required: true, wide: true },
      ],
    },
  },
  {
    id: "email-form",
    title: "Email form",
    component: "email_form",
    rendererPath: "prototype/frontend/widget/src/cards/EmailFormCard.tsx",
    data: {
      title: "Confirm your email",
      subtitle: "We will use this to look up your order.",
      submitLabel: "Continue",
      fields: [
        { id: "email", label: "Email", type: "email", required: true, wide: true },
      ],
    },
  },
  {
    id: "coupon-input",
    title: "Coupon input",
    component: "coupon_input",
    rendererPath: "prototype/frontend/widget/src/cards/CouponInputCard.tsx",
    data: {
      title: "Enter coupon code",
      subtitle: "Paste your coupon to apply it to the basket.",
      submitLabel: "Apply coupon",
      fields: [
        { id: "code", label: "Coupon code", required: true, wide: true, placeholder: "e.g. OLED10" },
      ],
    },
  },
  {
    id: "confirmation",
    title: "Confirmation",
    component: "confirmation",
    rendererPath: "prototype/frontend/widget/src/cards/ConfirmationCard.tsx",
    data: {
      title: "Continue to basket?",
      prompt: "Your coupon is ready. Would you like to go to the basket now?",
      cancelLabel: "Not now",
      yesLabel: "Go to basket",
      argsPretty: '{"target":"_top"}',
    },
  },
  {
    id: "spec-summary",
    title: "Spec table — key summary",
    component: "spec_table",
    note: "Single-product key spec mode with inline callouts.",
    rendererPath: "prototype/frontend/widget/src/cards/SpecTableCard.tsx",
    data: {
      sku: "OLED55C4PLA",
      productName: "LG OLED evo C4 55\"",
      titleMode: "key",
      // AISASSIT-144 검증: 강조(핑크 그라데이션) 값이 bold 아닌 Regular 인지 확인용.
      highlightSpecCodes: ["PANEL", "REFRESH_RATE"],
      specs: [
        { specCode: "SCREEN_SIZE", label: "Screen size", value: "55\"", keyTier: 1 },
        { specCode: "PANEL", label: "Panel", value: "OLED evo", keyTier: 1 },
        { specCode: "REFRESH_RATE", label: "Refresh rate", value: "144Hz", keyTier: 1 },
      ],
      strengths: [
        "OLED evo improves contrast and black levels for movie nights.",
        "144Hz support keeps fast gaming smoother and more responsive.",
        "The 55-inch size fits most living rooms without overwhelming the space.",
      ],
      strengthCallouts: [
        { specCode: "PANEL", text: "OLED evo improves contrast and black levels for movie nights." },
        { specCode: "REFRESH_RATE", text: "144Hz support keeps fast gaming smoother and more responsive." },
        { specCode: "SCREEN_SIZE", text: "The 55-inch size fits most living rooms without overwhelming the space." },
      ],
    },
  },
  {
    // AISASSIT-163 로컬 검증용(멀티그룹 + callout 혼합, Figma 2571 gap 측정). local-only.
    id: "spec-163-verify",
    title: "Spec — 163 gap verify",
    component: "spec_table",
    note: "AISASSIT-163 gap verify (Gaming/Picture groups).",
    rendererPath: "prototype/frontend/widget/src/cards/SpecTableCard.tsx",
    data: {
      sku: "OLED55C4PLA",
      productName: "LG OLED evo C4 55\"",
      titleMode: "key",
      specs: [
        { specCode: "FREESYNC", label: "FreeSync Compatible (AMD)", value: "Yes", category: "Gaming" },
        { specCode: "GSYNC", label: "G-Sync Compatible (Nvidia)", value: "Yes", category: "Gaming" },
        { specCode: "DISP_TYPE", label: "Display Type", value: "4K OLED", category: "Picture (Display)" },
        { specCode: "REFRESH", label: "Refresh Rate", value: "120Hz Native (VRR 165Hz)", category: "Picture (Display)" },
        { specCode: "GAMUT", label: "Wide Colour Gamut", value: "Perfect Colour", category: "Picture (Display)" },
      ],
      strengthCallouts: [
        { specCode: "GSYNC", text: "G-Sync Compatible is NVIDIA's screen synchronization technology that prevents screen tearing and stuttering. Unlike the original G-Sync, it is implemented using software, making it more affordable." },
        { specCode: "REFRESH", text: "It operates at a very smooth 120Hz by default for normal use (web surfing, videos), but when playing high-end games, smoothness can be maximized up to 165Hz with tear-prevention technology (VRR)." },
      ],
    },
  },
  {
    id: "spec-energy-only",
    title: "Spec — energy grade (UK)",
    component: "spec_table",
    note: "§4-6 energy-only: SKU title + btn-text PDF link-out (Figma 6476-33558).",
    rendererPath: "prototype/frontend/widget/src/cards/SpecTableCard.tsx",
    data: {
      sku: "OLED65G64LW",
      locale: "uk",
      isEnergyOnly: true,
      specs: [],
      totalCount: 0,
      energyDocs: [
        {
          originalName: "UK Energy label 2019",
          docId: "20260312278753",
          fileName: "OLED65G64LW_EK.pdf",
        },
        {
          originalName: "EU Energy label 2019",
          docId: "20260312278754",
          fileName: "OLED65G64LW_EU.pdf",
        },
      ],
    },
  },
  {
    id: "spec-bundle",
    title: "Spec table — bundle",
    component: "spec_table",
    note: "Bundle mode renders component-by-component accordions.",
    rendererPath: "prototype/frontend/widget/src/cards/SpecTableCard.tsx",
    data: {
      sku: "BUNDLE-TV-SNDBR",
      isBundle: true,
      titleMode: "key",
      bundleSpecs: [
        {
          sku: "OLED55C4PLA",
          name: "StandbyME2",
          specs: [
            { specCode: "FREESYNC", label: "FreeSync Compatible (AMD)", value: "Yes", category: "Gaming" },
            { specCode: "GSYNC", label: "G-Sync Compatible (Nvidia)", value: "Yes", category: "Gaming" },
            { specCode: "PANEL", label: "Display Type", value: "4K OLED", category: "Picture (Display)" },
            { specCode: "REFRESH_RATE", label: "Refresh Rate", value: "120Hz Native (VRR 165Hz)", category: "Picture (Display)" },
            { specCode: "DOLBY_ATMOS", label: "Dolby Atmos", value: "Yes", category: "Audio" },
            { specCode: "SPEAKER", label: "Speaker System", value: "4.2 channel", category: "Audio" },
          ],
          strengthCallouts: [
            { specCode: "GSYNC", text: "G-Sync Compatible is NVIDIA's screen synchronization technology that prevents screen tearing and stuttering. Unlike the original G-Sync, it is implemented using software, making it more affordable." },
            { specCode: "REFRESH_RATE", text: "It operates at a very smooth 120Hz by default for normal use (web surfing, videos), but when playing high-end games, smoothness can be maximized up to 165Hz with tear-prevention technology (VRR)." },
            { specCode: "DOLBY_ATMOS", text: "When Dolby Atmos is supported, sound is placed as an object in 3D space rather than as a 'channel'. It creates the sensation of a helicopter flying overhead or the sound of rain surrounding you from above and the sides." },
          ],
        },
        {
          sku: "S80TR",
          name: "LG Soundbar S80TR",
          specs: [
            { specCode: "CHANNELS", label: "Channels", value: "5.1.3ch", category: "Audio" },
            { specCode: "AUDIO", label: "Audio", value: "Dolby Atmos", category: "Audio" },
          ],
        },
      ],
      specs: [],
    },
  },
  {
    id: "spec-comparison-table",
    title: "Spec comparison table",
    component: "spec_comparison_table",
    note: "Alias of spec_table for multi-model table mode.",
    rendererPath: "prototype/frontend/widget/src/cards/SpecTableCard.tsx",
    data: {
      sku: "OLED55C4PLA",
      titleMode: "detail",
      focusSpecLabel: "Display & gaming",
      models: ["OLED55C4PLA", "QNED86A1A6A"],
      comparisons: [
        {
          category: "Display",
          specs: [
            { specCode: "PANEL", label: "Panel", values: { OLED55C4PLA: "OLED evo", QNED86A1A6A: "QNED" } },
            { specCode: "REFRESH_RATE", label: "Refresh rate", values: { OLED55C4PLA: "144Hz", QNED86A1A6A: "120Hz" } },
          ],
        },
      ],
      specs: [],
    },
  },
  {
    id: "ai-top-picks",
    title: "AI top picks",
    component: "ai_top_picks",
    rendererPath: "prototype/frontend/widget/src/cards/AiTopPicksCard.tsx",
    data: {
      products: [
        {
          sku: "OLED55C4PLA",
          name: "LG OLED evo C4 55\" 4K Smart TV",
          image: svgImage("OLED55C4PLA"),
          price,
          rating: 4.8,
          reviewCount: 1450,
          badges: [{ label: "Best match" }],
          keyFeatures: ["OLED evo panel", "144Hz gaming"],
          isAnchor: true,
          pdpUrl: "https://www.lg.com/uk/tvs-soundbars/oled-evo/oled55c4pla/",
        },
        {
          sku: "QNED86A1A6A",
          name: "LG QNED86 55\" 4K Smart TV",
          image: svgImage("QNED86A1A6A", "#f2f5ec"),
          price: altPrice,
          rating: 4.5,
          reviewCount: 932,
          badges: [{ label: "Bright room" }],
          keyFeatures: ["MiniLED backlight", "120Hz panel"],
          pdpUrl: "https://www.lg.com/uk/tvs-soundbars/qned/qned86a1a6a/",
        },
      ],
    },
  },
  {
    id: "compare-table",
    title: "Compare table",
    component: "compare_table",
    rendererPath: "prototype/frontend/widget/src/cards/CompareTableCard.tsx",
    data: {
      products: [
        {
          sku: "OLED55C4PLA",
          name: "LG OLED evo C4 55\"",
          image: svgImage("OLED55C4PLA"),
          price,
          pdpUrl: "https://www.lg.com/uk/tvs-soundbars/oled-evo/oled55c4pla/",
        },
        {
          sku: "QNED86A1A6A",
          name: "LG QNED86 55\"",
          image: svgImage("QNED86A1A6A", "#f2f5ec"),
          price: altPrice,
          pdpUrl: "https://www.lg.com/uk/tvs-soundbars/qned/qned86a1a6a/",
        },
      ],
      columns: [
        { specCode: "PANEL", label: "Panel", values: { OLED55C4PLA: "OLED evo", QNED86A1A6A: "QNED" }, isInterest: true },
        { specCode: "REFRESH_RATE", label: "Refresh rate", unit: "Hz", values: { OLED55C4PLA: "144", QNED86A1A6A: "120" }, bestSku: "OLED55C4PLA" },
        { specCode: "HDR", label: "HDR", values: { OLED55C4PLA: "Dolby Vision", QNED86A1A6A: "HDR10" } },
        // AISASSIT-100: 두 제품 값이 동일한데 bestSku 가 한쪽에 지정된 케이스 — 볼드가 안 떠야 정상(수정 전엔 1개만 볼드).
        { specCode: "SMART_TV", label: "Smart TV", values: { OLED55C4PLA: "webOS 24", QNED86A1A6A: "webOS 24" }, bestSku: "OLED55C4PLA" },
      ],
      metadata: { highlightColumns: ["PANEL"] },
    },
  },
  // 06/16 삭제 요청
  // {
  //   id: "promotion-products",
  //   title: "Promotion products",
  //   component: "promotion_products",
  //   rendererPath: "prototype/frontend/widget/src/cards/PromotionProductsCard.tsx",
  //   data: {
  //     groups: [
  //       {
  //         promotionId: "promo-1",
  //         promotionTitle: "Spring TV Sale",
  //         productCount: 2,
  //         products: [
  //           {
  //             sku: "OLED55C4PLA",
  //             name: "LG OLED evo C4 55\"",
  //             image: svgImage("OLED55C4PLA"),
  //             price,
  //           },
  //           {
  //             sku: "QNED86A1A6A",
  //             name: "LG QNED86 55\"",
  //             image: svgImage("QNED86A1A6A", "#f2f5ec"),
  //             price: altPrice,
  //           },
  //         ],
  //       },
  //     ],
  //   },
  // },
  {
    id: "cart-confirm-preview",
    title: "Cart confirm preview",
    component: "cart_confirm_preview",
    note: "Dedicated cart confirm preview renderer with add-cart style product list.",
    rendererPath: "prototype/frontend/widget/src/cards/CartConfirmPreviewCard.tsx",
    data: {
      products: [
        {
          sku: "S80TR",
          name: "LG Soundbar S80TR",
          image: svgImage("S80TR", "#efefef"),
          price: { currencySymbol: "£", amount: 699, originalAmount: 799, discountPct: 12 },
          flags: [{ label: "New", tone: "default" }],
        },
      ],
    },
  },
  {
    id: "simple-product-carousel",
    title: "Simple product carousel",
    component: "simple_product_carousel",
    note: "Same renderer, here shown in clarify-spec mode.",
    rendererPath: "prototype/frontend/widget/src/cards/SimpleProductCard.tsx",
    data: {
      capability: "spec",
      products: [
        {
          sku: "OLED55C4PLA",
          name: "LG OLED evo C4 55\"",
          image: svgImage("OLED55C4PLA"),
          price,
          // Figma eyebrow(사진 밑): accent "20% off" + 구분선 + main "New"
          flags: [
            { text: "20% off", highlighted: true },
            { text: "New" },
          ],
        },
        {
          sku: "QNED86A1A6A",
          name: "LG QNED86 55\"",
          image: svgImage("QNED86A1A6A", "#f2f5ec"),
          price: altPrice,
          flags: [{ text: "Trade-Up for £50 off", highlighted: true }],
        },
        {
          sku: "OLED42C4PLA",
          name: "LG OLED evo C4 42\"",
          image: svgImage("OLED42C4PLA", "#eef2f8"),
          price,
          flags: [{ text: "New" }],
        },
      ],
    },
  },
  {
    id: "cta-button",
    title: "CTA button",
    component: "cta_button",
    rendererPath: "prototype/frontend/widget/src/cards/CtaButtonCard.tsx",
    data: {
      label: "Go to basket",
      ctaKey: "go_to_basket",
      style: "primary",
      navigateUrl: "https://www.lg.com/uk/cart",
    },
  },
  {
    id: "live-chat-cta",
    title: "Live chat CTA",
    component: "live_chat_cta",
    rendererPath: "prototype/frontend/widget/src/cards/LiveChatCtaCard.tsx",
    data: {
      locale: "uk",
      showGoToBasket: true,
      showLiveChat: true,
      showEmail: true,
      goToBasketUrl: "https://www.lg.com/uk/cart",
      emailContactUrl: "https://www.lg.com/uk/support/email-to-president/",
    },
  },
  {
    id: "chat-summary-nudge",
    title: "Ask LG Proposal Scenario nudge",
    component: "chat_summary_nudge",
    rendererPath: "prototype/frontend/widget/src/cards/ChatSummaryNudgeCard.tsx",
    data: {
      locale: "uk",
      variant: "scenario",
      defaultEmail: "shopper@example.com",
      labels: {
        scenarioTitle: "Take it with you?",
        scenarioBody:
          "I can email you an Ask LG Proposal highlighting the main products we've focused on so far.",
        proposalCta: "Get My Proposal",
        modalTitle: "Your Ask LG Proposal",
        modalBodyGuest:
          "Where should we send your proposal? Please enter your email address below to receive a personalized summary of our chat.",
        modalBodyLoggedIn:
          "We'll send your proposal to the email below. Feel free to update the address if you'd prefer to receive your personalized summary elsewhere.",
        close: "Close",
        submit: "Submit",
        consentLabel: "I agree to the terms regarding personal information.",
        privacyNotice:
          "We'll email you a proposal based on this chat. See our Privacy Policy for how we use your email.",
        privacyNoticeLink: "Privacy Policy",
        privacyPolicyUrl: "https://www.lg.com/uk/privacy/#tabs-privacy-policy",
        invalidEmailFormat: "Please enter a valid email address. (e.g. example@email.com)",
        // showcase 샘플 — 런타임은 서버 i18n labels. EN only.
        successTitle: "Your Proposal is on its way!",
        successBody:
          "Your personalized summary and recommendations will be sent to your email shortly. If it doesn't arrive within a minute, please check your spam folder.",
        confirm: "Confirm",
        consentTermsLink: "terms regarding personal information",
      },
    },
  },
  {
    id: "login-nudge",
    title: "Login nudge (로그인 유도)",
    component: "login_nudge",
    rendererPath: "prototype/frontend/widget/src/cards/LoginNudgeCard.tsx",
    note: "IS-04: guide ai-lists .ac-login-nudge — Sign in + Join us.",
    data: {
      locale: "uk",
      joinUrl: "https://www.lg.com/uk/my-lg/create-account",
      loginUrl: "https://www.lg.com/uk/login",
      labels: {
        title: "Unlock Member Rewards",
        body: "Sign up free for welcome offers, member-only deals and more",
        joinCta: "Join us",
        signInCta: "Sign in",
      },
    },
  },
  {
    id: "find-my-model",
    title: "Find My Model (소모품/모델 조회)",
    component: "find_my_model",
    rendererPath: "prototype/frontend/widget/src/cards/FindMyModelCard.tsx",
    note: "AISASSIT-44: Figma 2729-5934/5971 재구현(플로팅 라벨 드롭다운 + Search 게이팅). selects/contents 는 백엔드 미연결 시 오프라인 fallback 용.",
    data: {
      locale: "uk",
      selects: [
        {
          id: "category",
          options: [
            { label: "TV/Audio/Video", value: "tv-audio-video" },
            { label: "Home Appliances", value: "home-appliances" },
            { label: "Computer Products", value: "computer-products" },
          ],
        },
        {
          id: "product",
          options: [
            { label: "TVs & Soundbars", value: "tvs-soundbars" },
            { label: "Refrigerators", value: "refrigerators" },
            { label: "Washing Machines", value: "washing-machines" },
          ],
        },
      ],
      contents: {
        "tvs-soundbars": {
          alt: "Model number location on TV",
          description:
            "The model number is printed on a label on the back of the TV.<br/>It also appears in Settings > Support > TV Information.",
        },
        "refrigerators": {
          alt: "Serial number location on refrigerator",
          description:
            "The model/serial number label is inside the refrigerator, on the upper-left side wall.",
        },
        "washing-machines": {
          alt: "Model number location on washing machine",
          description:
            "The label is on the door frame (visible when the door is open) or on the rear panel.",
        },
      },
    },
  },
  {
    id: "concept-compare-table",
    title: "Concept compare table (카테고리 개념 비교)",
    component: "concept_compare_table",
    note: "AISASSIT-145: 가로스크롤 검증용. 컬럼 min-width 합이 버블 폭을 넘으면 스크롤.",
    rendererPath: "prototype/frontend/widget/src/cards/ConceptCompareTableCard.tsx",
    data: {
      mode: "compare",
      concepts: [
        { id: "oled", label: "OLED" },
        { id: "qned", label: "QNED" },
      ],
      specDiff: [
        {
          label: "Picture creation method",
          values: {
            oled: "Self-lit pixels (no backlight), leading to perfect blacks and infinite contrast.",
            qned: "Mini-LED backlight with Quantum Dot + NanoCell for bright, vivid colour.",
          },
        },
        {
          label: "Best viewing conditions",
          values: {
            oled: "Ideal for dark, dedicated home-cinema environments.",
            qned: "Great for bright, well-lit living rooms with lots of ambient light.",
          },
        },
        {
          label: "What LG highlights most",
          values: {
            oled: "Perfect blacks and infinite contrast.",
            qned: "Peak brightness and vibrant colour volume.",
          },
        },
      ],
    },
  },
  {
    id: "delivery-comparison-table",
    title: "Delivery comparison table (배송 비교 테이블)",
    component: "delivery_comparison_table",
    note: "17_제품_Shipping §3 / ai-lists IS-05: 배송 유형=열 · 항목=행. tbody 2단 rowspan + 가로스크롤 + Disclaimer + PBP CTA.",
    rendererPath: "prototype/frontend/widget/src/cards/DeliveryComparisonTableCard.tsx",
    data: {
      ok: true,
      trade_up: true,
      pbp_cta: true,
      table: {
        layout: "table",
        columns: [
          { key: "rule_name", label: "Delivery", depth: 1 },
          { key: "price", label: "Price", depth: 1 },
          {
            key: "install:WALL_MOUNT",
            label: "Installation",
            sub_label: "Wall-mounting",
            depth: 2,
          },
          {
            key: "install:PLUG_PLAY",
            label: "Installation",
            sub_label: "Plug & Play",
            depth: 2,
          },
          {
            key: "haulaway:HAULAWAY",
            label: "Disposal",
            sub_label: "Haulaway",
            depth: 2,
          },
          {
            key: "timeslot:MORNING",
            label: "Timed Delivery",
            sub_label: "Morning (09:00 - 13:00)",
            depth: 2,
          },
          { key: "earliest_delivery", label: "Earliest Delivery Date", depth: 1 },
        ],
        rows: [
          {
            rule_id: "R1",
            rule_name: "Standard Delivery",
            price: "$0.00",
            "install:WALL_MOUNT": "Wall-mounting ($120.00)",
            "install:PLUG_PLAY": "Plug & Play ($30.00)",
            "haulaway:HAULAWAY": "Haulaway ($10.00)",
            "timeslot:MORNING": "-",
            earliest_delivery: "9 Sep",
          },
          {
            rule_id: "R2",
            rule_name: "Timed Delivery",
            price: "$15.00",
            "install:WALL_MOUNT": "-",
            "install:PLUG_PLAY": "Plug & Play ($30.00)",
            "haulaway:HAULAWAY": "-",
            "timeslot:MORNING": "Morning (09:00 - 13:00) ($15.00)",
            earliest_delivery: "9 Sep ($9.99), 10 Sep (Free)",
          },
        ],
        disclaimer:
          "Delivery dates are estimates and may vary. Installation and disposal services are subject to availability in your area.",
      },
      metadata: {
        pbp_url: "https://www.lg.com/uk/buy/sample-product/",
      },
    },
  },
];
